import { useExperimentContext } from '@rhg/hooks/use-experiments-context';
import { AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE } from '@rhg/libs/feature-flags';
import { useEffect, useState } from 'react';

// Amplitude types `variant.payload` as `any`; this hook's contract is that the
// payload is the caller's `Payload` shape (constrained to a record). Verify it
// is an object at runtime before handing it back as `Payload`.
const isPayload = <P extends Record<string, unknown>>(
    value: unknown
): value is P => typeof value === 'object' && value !== null;

interface UseAmplitudeExperimentProps {
    /** The name of the feature flag or experiment to check. */
    experimentName: string;
    /** If true, will not check the experiment client and return false immediately. */
    shortCircuit?: boolean;
    /**
     * If false, reads the variant without sending an `[Experiment] Exposure`
     * event. Pair it with a normal read where the user encounters the variant.
     * Defaults to true.
     */
    trackExposure?: boolean;
}

/**
 * Custom hook to check if an Amplitude feature-flag is enabled OR if the user is enrolled in an Amplitude experiment.
 * @template Payload - The expected shape of the experiment payload. Defaults to Record<string, unknown>.
 * @example const { isEnrolled, isLoading } = useAmplitudeExperiment({experimentName: AmplitudeFeatureFlagNames.MY_EXPERIMENT});
 * @example const { payload } = useAmplitudeExperiment<{launchDate?: string}>({experimentName: 'my-flag'});
 *
 * See the README > "Feature Flag setup requirements" for Amplitude flag set up requirements.
 */

export const useAmplitudeExperiment = <
    Payload extends Record<string, unknown> = Record<string, unknown>
>({
    experimentName,
    shortCircuit,
    trackExposure = true,
}: UseAmplitudeExperimentProps) => {
    const { experimentClient, experimentUser, isExperimentUserReady } =
        useExperimentContext();

    const [isEnrolled, setIsEnrolled] = useState<boolean>();
    const [isLoading, setIsLoading] = useState(true);
    const [isFetched, setIsFetched] = useState(false);
    const [variant, setVariant] = useState<string | undefined>();
    const [payload, setPayload] = useState<Payload | undefined>();
    const [evaluatedPartnerId, setEvaluatedPartnerId] = useState<
        string | undefined
    >();

    useEffect(() => {
        if (shortCircuit) {
            setIsEnrolled(false);
            setIsLoading(false);
            setIsFetched(true);
            return;
        }

        if (experimentClient === AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE) {
            return; // still loading — don't report a result yet
        }

        if (!experimentClient) return;

        if (!isExperimentUserReady) {
            // Readiness can drop after a first evaluation (e.g. a user
            // property like isAdult is being re-synced). Report loading so
            // consumers don't act on the stale result.
            setIsLoading(true);
            return;
        }

        try {
            const variant = trackExposure
                ? experimentClient.variant(experimentName)
                : experimentClient.all()[experimentName];
            setIsEnrolled(
                variant?.value === 'treatment' || variant?.value === 'on'
            );
            const rawPayload: unknown = variant?.payload;
            setPayload(isPayload<Payload>(rawPayload) ? rawPayload : undefined);
            setVariant(variant?.value);
        } catch {
            setIsEnrolled(false);
        } finally {
            setIsLoading(false);
            setIsFetched(true);
            setEvaluatedPartnerId(
                experimentUser?.user_properties?.partnerId as string | undefined
            );
        }
    }, [
        experimentClient,
        experimentName,
        shortCircuit,
        trackExposure,
        experimentUser,
        isExperimentUserReady,
    ]);

    return {
        isLoading,
        isFetched,
        isEnrolled,
        variant,
        payload,
        evaluatedPartnerId,
    };
};
